import { useState, useRef, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { DrawerItem } from 'helpers/drawer-config';
import { searchMenuItems, SearchResult } from 'helpers/menu-search';
import { useGlobalContext } from 'context/GlobalContext';

type SearchBarProps = {
  allMenuConfig: DrawerItem[];
};

/**
 * SearchBar Component
 *
 * Barra de búsqueda jerárquica con icono de lupa.
 * Permite buscar en todos los niveles del menú y navegar directamente.
 *
 * @component
 * @param {SearchBarProps} props - Las propiedades del componente
 * @returns {JSX.Element} El componente de búsqueda
 */
function SearchBar({ allMenuConfig }: SearchBarProps): JSX.Element {
  const { t } = useTranslation('global');
  const navigate = useNavigate();
  const { hasPerm } = useGlobalContext();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
        setResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      const searchResults = searchMenuItems(
        searchQuery,
        allMenuConfig,
        hasPerm
      );
      setResults(searchResults);
    } else {
      setResults([]);
    }
  }, [searchQuery, allMenuConfig, hasPerm]);

  const handleSearchClick = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
    setResults([]);
  };

  const handleResultClick = (path: string) => {
    navigate(path);
    handleClose();
  };

  return (
    <Box ref={searchRef} sx={{ position: 'relative' }}>
      {!isOpen ? (
        <IconButton
          onClick={handleSearchClick}
          sx={{
            color: '#0E335C',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <SearchIcon />
        </IconButton>
      ) : (
        <Box>
          <Paper
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: 300,
              px: 1,
              py: 0.5,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <SearchIcon sx={{ color: '#666', mr: 1 }} />
            <InputBase
              autoFocus
              placeholder={t('topNav.search') || 'Buscar en el menú...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, fontSize: '14px' }}
            />
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>

          {/* Resultados de búsqueda */}
          {results.length > 0 && (
            <Paper
              sx={{
                position: 'absolute',
                top: '100%',
                right: 0,
                width: 400,
                maxHeight: 400,
                overflow: 'auto',
                mt: 0.5,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                zIndex: 1300
              }}
            >
              <List sx={{ py: 0 }}>
                {results.map((result, index) => (
                  <ListItem
                    key={`${result.path}-${index}`}
                    button
                    onClick={() => handleResultClick(result.path)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: '1px solid #f0f0f0',
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      },
                      '&:last-child': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    <ListItemText
                      primary={result.breadcrumb}
                      primaryTypographyProps={{
                        fontSize: '14px',
                        color: '#0E335C'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* Sin resultados */}
          {searchQuery.length >= 3 && results.length === 0 && (
            <Paper
              sx={{
                position: 'absolute',
                top: '100%',
                right: 0,
                width: 300,
                mt: 0.5,
                p: 2,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                zIndex: 1300
              }}
            >
              <Box sx={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
                {t('topNav.noResults') || 'No se encontraron resultados'}
              </Box>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}

export default SearchBar;

